import { TooltipShield } from '@/components/admin/tooltip-shield';
import { cn } from '@/lib/utils';
import { Icon, Tooltip } from '@pms-digital/design-system';

/**
 * The gutter's copy icon - the « Dupliquer » gesture of the items a page
 * duplicates in place (refonte modulaire §4.2; issue #117).
 *
 * §4.2 fixes the shape and leaves nothing for a screen to decide: « une **icône
 * de copie dans la gouttière, juste avant la croix** de retrait, et rien
 * d'autre à découvrir ». Four gutters mount this component - a protocol's step,
 * objective and exit condition, a criterion's example - and they live in two
 * screens written by two hands. An icon chosen four times would be four icons;
 * this component is what makes « même icône, même place, partout » checkable
 * rather than promised.
 *
 * The icon is the Material Symbol `content_copy` (it was lucide's `Copy`), and that is a convention shared by
 * the three duplication lots (#116, #117, #118) rather than a choice made in
 * this file: one glyph for one gesture, on an entity's card, in a gutter, and
 * in the « Dupliquer » entry of a step's menu.
 *
 * ## The fifth gutter is NOT here, and that is deliberate
 *
 * A global rule's gutter (Réglages > Règles, §11.3) stacks four controls that
 * are all the bordered `IconButton` of `reglages/reference-list.tsx` - up,
 * down, duplicate, remove. Re-spelling that button's classes here would put the
 * design system's outlined icon button in two files, and the two would drift on
 * the first change. So that screen keeps its own `IconButton`, which gained the
 * one thing it was missing for this lot (`blocked`), and passes the same `Copy`
 * glyph.
 *
 * ## At the cap: DISABLED, wrapped in the shield
 *
 * At its list's cap the icon is « **désactivée et dit pourquoi au survol** »: a
 * gesture with no dialog has only that one place to refuse, and
 * clickable-then-refused is not an option when nothing opens to carry the
 * refusal.
 *
 * A `disabled` control receives neither pointer nor focus, so a bubble hung on
 * the button itself would never open - exactly when it is needed. That is what
 * `TooltipShield` exists for (`ui/tooltip.tsx`), and this is the idiom the
 * scoring band's cross already uses, reused here verbatim: the wrapper is
 * focusable while the control is closed, the control stays truly `disabled`,
 * and the bubble says the REASON when it is closed and the NAME when it is
 * open. Writing `aria-disabled` on a live button instead would be the
 * anti-pattern this component replaced - a control that still fires.
 *
 * ## It is ABSENT in read-only, and that is the caller's job
 *
 * « Absente en lecture seule, comme la croix » (§4.2): this component carries
 * no `readOnly`, because greying it would be keeping it. Each gutter omits it
 * exactly as it already omits its cross - `{readOnly ? null : ...}` - and the
 * criterion editor puts its `Gutter` spacer there instead, so the column keeps
 * its width.
 *
 * ## Two sizes, because gutters are not all the same width
 *
 * What is common is the glyph, the place and the refusal; the size is not, and
 * forcing it would break a layout. `compact` is the 28 px column of an exit
 * condition's card and of an objective's row; `gutter` is the 36 px one of a
 * step's head and of a criterion's example. Both are named here rather than
 * passed as a `className` by four callers, so that the day the design system
 * rules on a third, there is one file to read.
 *
 * ## The shield wraps only while the control is CLOSED
 *
 * Open, the bubble hangs on the button itself, which is what lets `Tooltip`
 * see an `aria-label` equal to its own text and drop the description rather
 * than have a screen reader read the word twice. Wrapped unconditionally, the
 * tooltip's child would always be the `<span>`, the guard could never fire, and
 * `aria-describedby` would point at a node ARIA does not inherit from. Same
 * shape as `reglages/reference-list.tsx`.
 */

export type DuplicateButtonShape = 'gutter' | 'compact';

export interface DuplicateButtonProps {
  /** The accessible name, AND what the bubble says while the gesture is open. */
  readonly label: string;
  /** Why the gesture is closed, or `null` when the list is not at its cap. */
  readonly blocked: string | null;
  readonly testId?: string;
  readonly shape?: DuplicateButtonShape;
  /** What the shield takes over from the gutter's own layout. */
  readonly className?: string;
  readonly onClick: () => void;
}

/**
 * The same recipe as the cross each one sits beside, including the closed
 * state - `disabled:` variants, because the button really is `disabled`.
 */
const SHAPES: Record<DuplicateButtonShape, string> = {
  gutter:
    'inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent disabled:hover:text-gray-300',
  compact:
    'inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent disabled:hover:text-gray-300',
};

const ICON_SIZES: Record<DuplicateButtonShape, 18 | 14> = {
  gutter: 18,
  compact: 14,
};

export function DuplicateButton({
  label,
  blocked,
  testId,
  shape = 'gutter',
  className,
  onClick,
}: DuplicateButtonProps) {
  const closed = blocked !== null;
  const control = (
    <button
      type="button"
      {...(testId === undefined ? {} : { 'data-testid': testId })}
      aria-label={label}
      disabled={closed}
      onClick={onClick}
      className={cn(SHAPES[shape], closed ? undefined : className)}
    >
      <Icon name="content_copy" size={ICON_SIZES[shape]} />
    </button>
  );
  return (
    <Tooltip content={closed ? blocked : label}>
      {closed ? (
        <TooltipShield closed layout={cn('shrink-0', className)}>
          {control}
        </TooltipShield>
      ) : (
        control
      )}
    </Tooltip>
  );
}
